/**
 * VERA-1095 Slice 4 — /signup/success page.
 *
 * Stripe redirects here after Checkout with ?session_id=<id>.
 * We POST /signup/verify, which activates the pending subscription.
 *
 * States:
 *   loading   — calling the verify endpoint
 *   success   — activated:true → payment confirmed; invitation email already
 *               sent at provision time (WorkOS) — founder accepts to sign in
 *   pending   — activated:false (Stripe hasn't settled yet) → auto-retry up
 *               to MAX_RETRIES times with exponential back-off
 *   error     — unrecoverable (session not found, etc.)
 *
 * Email verification: the founder proves email ownership by accepting the
 * WorkOS invitation sent at provision time — no separate OTP step needed.
 *
 * TODO(VERA-1095): Add a Stripe webhook for async activation so founders who
 * close the tab before landing here still get activated.
 */

import React, {useEffect, useRef, useState} from 'react';
import {Box, CircularProgress, Stack, Typography} from '@mui/material';
import {Button} from '@repo/ui';
import {createApiCall} from 'services';
import {ROUTES} from 'routes';
import { VERA_SUPPORT_EMAIL } from 'constants/support';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VerifyResult {
  activated: boolean;
  companyId: string;
  status: string;
}

type PageState = 'loading' | 'success' | 'pending' | 'error';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_RETRIES = 5;
const RETRY_BASE_MS = 2000; // 2 s, doubling each attempt (2 → 4 → 8 → 16 → 32 s)

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------

async function verifyCheckout(session_id: string): Promise<VerifyResult> {
  const res = await createApiCall({
    method: 'POST',
    url: '/signup/verify',
    data: {session_id},
  });
  if (res.status === 404) {
    throw new Error('No pending subscription found for this checkout session.');
  }
  if (res.status >= 400) {
    throw new Error(res.data?.message || 'Verification failed. Please contact support.');
  }
  return res.data as VerifyResult;
}

// ---------------------------------------------------------------------------
// Shared shell (matches /signup page styling)
// ---------------------------------------------------------------------------

const PageShell: React.FC<{children: React.ReactNode}> = ({children}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      px: 3,
      py: 6,
      bgcolor: 'background.default',
    }}
  >
    <Box sx={{width: '100%', maxWidth: 440}}>
      <Typography variant="h5" fontWeight={700} mb={1} textAlign="center">
        Create your Vera account
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={4}>
        Vera — digital product certificates for premium brands
      </Typography>
      {children}
    </Box>
  </Box>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SignupSuccessPage: React.FC = () => {
  const [state, setState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const retryCount = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Read session_id from the URL query string — Stripe substitutes it at
  // redirect time (success_url = .../signup/success?session_id={CHECKOUT_SESSION_ID})
  const sessionId = new URLSearchParams(window.location.search).get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      setErrorMessage('Missing checkout session. Please contact support if you completed payment.');
      return;
    }

    const attempt = async () => {
      try {
        const result = await verifyCheckout(sessionId);
        if (result.activated) {
          setState('success');
        } else {
          // Payment not yet settled — retry with back-off
          if (retryCount.current < MAX_RETRIES) {
            setState('pending');
            const delay = RETRY_BASE_MS * Math.pow(2, retryCount.current);
            retryCount.current += 1;
            timerRef.current = setTimeout(attempt, delay);
          } else {
            // Exhausted retries — show pending state with manual retry option
            setState('pending');
          }
        }
      } catch (err: any) {
        setState('error');
        setErrorMessage(err?.message || 'Something went wrong. Please contact support.');
      }
    };

    attempt();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [sessionId]);

  const handleManualRetry = () => {
    retryCount.current = 0;
    setState('loading');
    if (!sessionId) return;
    verifyCheckout(sessionId)
      .then(result => {
        if (result.activated) {
          setState('success');
        } else {
          setState('pending');
        }
      })
      .catch(err => {
        setState('error');
        setErrorMessage(err?.message || 'Something went wrong. Please contact support.');
      });
  };

  // Loading
  if (state === 'loading') {
    return (
      <PageShell>
        <Box sx={{textAlign: 'center', py: 4}}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" mt={2}>
            Confirming your payment…
          </Typography>
        </Box>
      </PageShell>
    );
  }

  // Success
  if (state === 'success') {
    return (
      <PageShell>
        <Box
          sx={{
            textAlign: 'center',
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '16px',
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={1}>
            Payment received — your company is ready
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Check your email to finish setting up your account and sign in. We sent
            an invitation link when your company was created.
          </Typography>
          <Button
            variant="primary"
            onClick={() => {
              window.location.href = ROUTES.SIGN_IN;
            }}
          >
            Go to sign in
          </Button>
        </Box>
      </PageShell>
    );
  }

  // Pending / still-settling
  if (state === 'pending') {
    return (
      <PageShell>
        <Box
          sx={{
            textAlign: 'center',
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '16px',
          }}
        >
          <CircularProgress size={24} sx={{mb: 2}} />
          <Typography variant="h6" fontWeight={600} mb={1}>
            Finalizing your subscription…
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Your payment is processing. This usually takes a few seconds.
            {retryCount.current >= MAX_RETRIES && (
              ' If this message persists, try refreshing the page or contact support.'
            )}
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <Button variant="secondary" onClick={handleManualRetry}>
              Check again
            </Button>
          </Stack>
        </Box>
      </PageShell>
    );
  }

  // Error
  return (
    <PageShell>
      <Box
        sx={{
          textAlign: 'center',
          p: 3,
          border: '1px solid',
          borderColor: 'error.main',
          borderRadius: '16px',
        }}
      >
        <Typography variant="h6" fontWeight={600} mb={1} color="error">
          Something went wrong
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {errorMessage ||
            'We could not confirm your payment. Please contact support at '}
          {!errorMessage && (
            <a href={`mailto:${VERA_SUPPORT_EMAIL}`} style={{color: 'inherit'}}>
              {VERA_SUPPORT_EMAIL}
            </a>
          )}
          {!errorMessage && '.'}
        </Typography>
        <Stack direction="row" spacing={1.5} justifyContent="center">
          <Button
            variant="secondary"
            onClick={handleManualRetry}
          >
            Try again
          </Button>
        </Stack>
      </Box>
    </PageShell>
  );
};

export default SignupSuccessPage;
